import { useApolloClient } from '@apollo/client';
import type { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { evictField } from '@/utils/graphqlUtils';

export interface UpdateUserTabPreferencesTRPCInput {
  ObjectType: Parent_Type_Enum;
  Preferences: unknown;
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 * (insert_user_tab_preference { affected_rows }).
 */
const mapTrpcResponseToGraphQL = () => ({
  insert_user_tab_preference: {
    __typename: 'user_tab_preference_mutation_response' as const,
    affected_rows: 1,
  },
});

export const useUpdateUserTabPreferencesTRPC = () => {
  const trpc = useTRPC();
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...trpc.frontend.userTabPreference.updateUserTabPreferences.mutationOptions(
      {
        onSuccess: async () => {
          // The Apollo `useTabPreferences` reader still queries
          // `user_tab_preference` directly — keep the Apollo eviction
          // in lockstep with the old GraphQL mutation's `update`
          // callback so existing readers refresh.
          evictField(apolloClient.cache, 'user_tab_preference');
          // COI-889: `useGetDefaultTabs` reads the preference rows through
          // `frontend.tab.defaults` (5-minute window), not the Apollo leg.
          await queryClient.invalidateQueries({
            queryKey: trpc.frontend.tab.pathKey(),
          });
        },
      }
    ),
  });

  return {
    updateUserTabPreferences: async (
      variables: UpdateUserTabPreferencesTRPCInput
    ) => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
